import { useCallback, useState } from 'react';
import type { PreviewSubscriptionPlan } from '../../components/upgrade';
import { useTrack } from '../useTrack';
import { getLogger } from '../../libs/logger';
import { TrackedEvents } from '../../features/tracking';
import { singletonHook } from 'react-singleton-hook';

const logger = getLogger('UpgradeSubscriptionEventTracking');

/* Base props */

type PlanLengthProps = {
  planSelected: PreviewSubscriptionPlan['name'];
  lengthSelected: PreviewSubscriptionPlan['period'];
};

export type PlanLengthSeatsProps = PlanLengthProps & {
  seats: number;
};

/* Individual event props */
type UpgradePromoCodeProps = PlanLengthSeatsProps & {
  promoCode: string | undefined;
};

type UpgradePlanLengthProps = {
  lengthSelected: PreviewSubscriptionPlan['period'];
  toggleFrequency: number;
};

type UpgradeRecaptchaFailedProps = PlanLengthSeatsProps & {
  timesFailed: number;
};

type StripeSourceFailedToAddDuringUpgradeProps = PlanLengthSeatsProps & {
  errorCode: string;
};

type UpgradePaymentFailedToInitiateProps = PlanLengthSeatsProps & {
  errorCode: string;
};

type UpgradeButtonClickedProps = {
  location: string;
  planSelected?: string;
};

type EventProps =
  | [TrackedEvents.UpgradeButtonClicked, UpgradeButtonClickedProps]
  | [TrackedEvents.BackButtonClickedOnPaymentSummaryPage, PlanLengthSeatsProps]
  | [TrackedEvents.UpgradePromoCodeAddedSuccessfully, UpgradePromoCodeProps]
  | [TrackedEvents.UpgradePromoCodeSelectedInvalid, UpgradePromoCodeProps]
  | [TrackedEvents.UpgradePromoCodeRemoved, UpgradePromoCodeProps]
  | [
      TrackedEvents.SubscriptionCycleChangedDuringPlanOverviewPage,
      UpgradePlanLengthProps,
    ]
  | [
      TrackedEvents.SubscriptionCycleChangedDuringPaymentSummaryPage,
      UpgradePlanLengthProps,
    ]
  | [TrackedEvents.DayPassPlanSelected, PlanLengthProps]
  | [
      TrackedEvents.UpgradePlanSelectedToMoveToPaymentSummaryPage,
      PlanLengthProps,
    ]
  | [
      TrackedEvents.DayPassTooltipViewedOnUpgradePlanOverviewPage,
      PlanLengthProps,
    ]
  | [TrackedEvents.UpgradeAbandonedDuringPlanOverviewPage]
  | [TrackedEvents.UpgradeAbandonedDuringPaymentSummaryPage]
  | [TrackedEvents.UpgradeRecaptchaCompletedSuccessfully, PlanLengthSeatsProps]
  | [TrackedEvents.UpgradeRecaptchaFailed, UpgradeRecaptchaFailedProps]
  | [
      TrackedEvents.SubscribeButtonClickedToMoveToConfirmationPage,
      PlanLengthSeatsProps,
    ]
  | [
      TrackedEvents.StripeSourceAddedSuccessfullyDuringUpgrade,
      PlanLengthSeatsProps,
    ]
  | [
      TrackedEvents.StripeSourceFailedToAddDuringUpgrade,
      StripeSourceFailedToAddDuringUpgradeProps,
    ]
  | [TrackedEvents.UpgradePaymentInitiatedSuccessfully, PlanLengthSeatsProps]
  | [
      TrackedEvents.UpgradePaymentFailedToInitiate,
      UpgradePaymentFailedToInitiateProps,
    ]
  | [TrackedEvents.UpgradePaymentSuccessfullyConfirmed, PlanLengthSeatsProps]
  | [TrackedEvents.UpgradePaymentFailedToConfirm, PlanLengthSeatsProps]
  | [TrackedEvents.UpgradePaymentCompletedSuccessfully, PlanLengthSeatsProps]
  | [TrackedEvents.UpgradePaymentFailedToComplete, PlanLengthSeatsProps]
  | [TrackedEvents.UpgradeFlowCompleted, PlanLengthSeatsProps]
  | [TrackedEvents.ClinicSetupCompleted, PlanLengthSeatsProps]
  | [TrackedEvents.ClinicSetupCompleted, PlanLengthSeatsProps]
  | [
      TrackedEvents.SeatsTooltipOnUpgradePaymentSummaryPageViewed,
      PlanLengthProps,
    ]
  | [
      TrackedEvents.SetupFeeTooltipOnUpgradePaymentSummaryPageViewed,
      PlanLengthProps,
    ]
  | [
      TrackedEvents.SalesTaxTooltipOnUpgradePaymentSummaryPageViewed,
      PlanLengthProps,
    ];

type TrackEvent = (...args: EventProps) => void;
type UseTrackUpgradeSubscriptionEventReturnValue = {
  trackEvent: TrackEvent;
  upgradeOrigin?: string;
};

const eventsContainsUpgradeOriginProp = new Set([
  TrackedEvents.UpgradeAbandonedDuringPlanOverviewPage,
  TrackedEvents.SubscriptionCycleChangedDuringPlanOverviewPage,
  TrackedEvents.SubscriptionCycleChangedDuringPaymentSummaryPage,
  TrackedEvents.DayPassTooltipViewedOnUpgradePlanOverviewPage,
  TrackedEvents.DayPassPlanSelected,
  TrackedEvents.UpgradePlanSelectedToMoveToPaymentSummaryPage,
  TrackedEvents.UpgradeAbandonedDuringPaymentSummaryPage,
  TrackedEvents.BackButtonClickedOnPaymentSummaryPage,
  TrackedEvents.SubscribeButtonClickedToMoveToConfirmationPage,
  TrackedEvents.StripeSourceAddedSuccessfullyDuringUpgrade,
  TrackedEvents.StripeSourceFailedToAddDuringUpgrade,
  TrackedEvents.UpgradePaymentInitiatedSuccessfully,
  TrackedEvents.UpgradePaymentFailedToInitiate,
  TrackedEvents.UpgradePaymentSuccessfullyConfirmed,
  TrackedEvents.UpgradePaymentFailedToConfirm,
  TrackedEvents.UpgradePaymentCompletedSuccessfully,
  TrackedEvents.UpgradePaymentFailedToComplete,
  TrackedEvents.UpgradeFlowCompleted,
  TrackedEvents.ClinicSetupCompleted,
  TrackedEvents.ClinicSetupAbandoned,
  TrackedEvents.SeatsTooltipOnUpgradePaymentSummaryPageViewed,
  TrackedEvents.SetupFeeTooltipOnUpgradePaymentSummaryPageViewed,
  TrackedEvents.SalesTaxTooltipOnUpgradePaymentSummaryPageViewed,
]);

export enum UpgradeButtonLocation {
  DashboardUpgradeButton = 'dashboard_upgrade_button',
  TextInviteUpgradeButton = 'text_invite_upgrade_button',
  EmailInviteUpgradeButton = 'email_invite_upgrade_button',
  SubscriptionTabUpgradeButton = 'subscription_tab_upgrade_button',
  SubscriptionTabChangeSubscriptionButton = 'subscription_tab_change_subscription_button',
  PreferencesTabUpgradeButton = 'preferences_tab_upgrade_button',
  WaitingRoomTabUpgradeButton = 'waiting_room_tab_upgrade_button',
  AppsUpgradeButton = 'apps_upgrade_button',
  PricingTableUpgradeButton = 'pricing_table_upgrade_button',
}

const hookInitValues = {
  upgradeOrigin: undefined,
  trackEvent: () => undefined,
};

const useTrackUpgradeSubscriptionEventImpl =
  (): UseTrackUpgradeSubscriptionEventReturnValue => {
    const track = useTrack();

    const [upgradeOrigin, setUpgradeOrigin] = useState<string | undefined>();

    const trackEvent: TrackEvent = useCallback(
      (eventName, data?) => {
        const additionalTrackingData = eventsContainsUpgradeOriginProp.has(
          eventName,
        )
          ? {
              upgradeOrigin,
            }
          : undefined;

        // DataDog
        logger.info(eventName, {
          ...additionalTrackingData,
          ...data,
        });

        // Analytics (Segment)
        void track(eventName, {
          ...additionalTrackingData,
          ...data,
        });

        if (data && 'location' in data) {
          setUpgradeOrigin(data.location);
        }
      },
      [upgradeOrigin],
    );

    return {
      trackEvent,
      upgradeOrigin,
    };
  };

export const useTrackUpgradeSubscriptionEvent =
  singletonHook<UseTrackUpgradeSubscriptionEventReturnValue>(
    hookInitValues,
    useTrackUpgradeSubscriptionEventImpl,
  );
